import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Segmented, Skeleton, Typography } from 'antd';
import { EnvironmentOutlined, ReloadOutlined } from '@ant-design/icons';
import { DashboardCard } from '../../../components/DashboardCard';
import { EmptyState } from '../../../components/EmptyState';
import { PagoCreateModal } from '../../pagos/components/PagoCreateModal';
import { useT } from '../../../locales';
import {
  formatCurrency,
  type ClienteRef,
  type CobradorCercaClienteRow,
} from '@aid/shared';
import { useCobradorCerca, useInvalidarCobrador } from '../api/useCobrador';
import {
  RADIO_CERCA_DEFECTO,
  RADIO_CERCA_OPCIONES,
  formatDistancia,
  type RadioCercaMetros,
} from '../lib/cerca';
import { CERCA_GEO_ESTADO, useCercaUbicacion, type CercaUbicacion } from './useCercaUbicacion';
import { CercaMapa } from './CercaMapa';
import { SectionHeader } from '../../../components/SectionHeader';
import './cerca.css';

const { Text } = Typography;

function cercaRowToClienteRef(cliente: CobradorCercaClienteRow): ClienteRef {
  return {
    id: cliente.idCliente,
    nombre: cliente.nombre,
    identificacion: null,
    ip: cliente.codigo,
  };
}

interface CercaRowProps {
  cliente: CobradorCercaClienteRow;
  selected: boolean;
  onSelect: () => void;
  rowRef: (node: HTMLButtonElement | null) => void;
}

function CercaRow({ cliente, selected, onSelect, rowRef }: CercaRowProps) {
  const t = useT();
  const nombre =
    cliente.nombre ?? t('dashboard.rutaCobros.clientFallback', { id: cliente.idCliente });
  const distancia = formatDistancia(cliente.distanciaMetros);

  return (
    <button
      ref={rowRef}
      type="button"
      data-testid={`cerca-row-${cliente.idCliente}`}
      className={`cerca-row${selected ? ' cerca-row--selected' : ''}`}
      aria-pressed={selected}
      aria-label={t('pages.cobrador.cerca.rowLabel', {
        nombre,
        deuda: formatCurrency(cliente.deudaTotal),
        distancia,
      })}
      onClick={onSelect}
    >
      <span className="cerca-row__mainline">
        <Text strong>{nombre}</Text>
        <Text strong className="cerca-row__monto">
          {formatCurrency(cliente.deudaTotal)}
        </Text>
      </span>
      <span className="cerca-row__detail">
        <Text type="secondary" style={{ fontSize: 12 }}>
          {distancia}
          {cliente.codigo ? ` · ${cliente.codigo}` : ''}
        </Text>
        <span className="cerca-row__cta" aria-hidden="true">
          {t('pages.cobrador.table.cobrar')}
        </span>
      </span>
    </button>
  );
}

/**
 * Tab `Cerca` de Mi Cobro: captura la posición del cobrador una vez (snapshot
 * del navegador, sin persistencia ni rastreo), consulta los clientes
 * pendientes con coordenadas dentro del radio y permite abrir el cobro
 * existente preselecto. Las visitas quedan fuera de alcance (CERC-02).
 */
export function CobradorCercaTab() {
  const t = useT();
  const { estado, ubicacion, capturar } = useCercaUbicacion();
  const [radioMetros, setRadioMetros] = useState<RadioCercaMetros>(RADIO_CERCA_DEFECTO);
  const [cobrarCliente, setCobrarCliente] = useState<CobradorCercaClienteRow | null>(null);
  const rowRefs = useRef(new Map<number, HTMLButtonElement>());
  const refrescar = useInvalidarCobrador();

  const cercaQuery = useCobradorCerca(ubicacion, radioMetros);
  const cercaData = cercaQuery.data;
  const clientes = cercaData?.data ?? [];
  const radioIndice = Math.max(0, RADIO_CERCA_OPCIONES.indexOf(radioMetros));

  // Al abrir el cobro preselecto, el focus vuelve a la fila del cliente
  // (misma mecánica que la lista de la ruta de cobros).
  useEffect(() => {
    if (cobrarCliente === null) return;
    const row = rowRefs.current.get(cobrarCliente.idCliente);
    row?.focus({ preventScroll: true });
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [cobrarCliente]);

  const onSeleccionar = (cliente: CobradorCercaClienteRow) => {
    setCobrarCliente(cliente);
  };

  const controles = (
    <div className="cerca-controls" aria-label={t('pages.cobrador.cerca.controlsLabel')}>
      <div className="cerca-controls__posicion">
        <Button
          icon={<EnvironmentOutlined />}
          loading={estado === CERCA_GEO_ESTADO.SOLICITANDO}
          onClick={capturar}
        >
          {ubicacion !== null
            ? t('pages.cobrador.cerca.actualizarUbicacion')
            : t('pages.cobrador.cerca.capturarUbicacion')}
        </Button>
        {ubicacion !== null && estado !== CERCA_GEO_ESTADO.SOLICITANDO && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('pages.cobrador.cerca.ubicacionCapturada', {
              lat: ubicacion.lat.toFixed(5),
              lng: ubicacion.lng.toFixed(5),
            })}
          </Text>
        )}
      </div>
      <div className="cerca-controls__radio" role="group" aria-label={t('pages.cobrador.cerca.radio')}>
        <Text type="secondary" style={{ fontSize: 13, fontWeight: 600 }}>
          {t('pages.cobrador.cerca.radio')}
        </Text>
        <Segmented
          options={RADIO_CERCA_OPCIONES.map((opcion, indice) => ({
            value: indice,
            label: formatDistancia(opcion),
          }))}
          value={radioIndice}
          onChange={(v) => {
            const opcion = RADIO_CERCA_OPCIONES[Number(v)];
            if (opcion !== undefined) setRadioMetros(opcion);
          }}
        />
      </div>
    </div>
  );

  if (estado !== CERCA_GEO_ESTADO.CAPTURADA && ubicacion === null) {
    switch (estado) {
      case CERCA_GEO_ESTADO.INACTIVA:
        return (
          <div>
            {controles}
            <div style={{ marginTop: 16 }}>
              <EmptyState
                title={t('pages.cobrador.cerca.sinUbicacionTitulo')}
                description={t('pages.cobrador.cerca.sinUbicacionDescripcion')}
                action={
                  <Button
                    type="primary"
                    icon={<EnvironmentOutlined />}
                    onClick={() => capturar()}
                  >
                    {t('pages.cobrador.cerca.capturarUbicacion')}
                  </Button>
                }
              />
            </div>
          </div>
        );
      case CERCA_GEO_ESTADO.SOLICITANDO:
        return (
          <div>
            {controles}
            <div style={{ marginTop: 16 }}>
              <Alert
                type="info"
                showIcon
                message={t('pages.cobrador.cerca.ubicacionSolicitando')}
              />
            </div>
          </div>
        );
      case CERCA_GEO_ESTADO.DENEGADA:
        return (
          <div>
            {controles}
            <div style={{ marginTop: 16 }}>
              <EmptyState
                title={t('pages.cobrador.cerca.geoDenegada')}
                description={t('pages.cobrador.cerca.geoDenegadaDescripcion')}
                action={
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => capturar()}
                  >
                    {t('common.retry')}
                  </Button>
                }
              />
            </div>
          </div>
        );
      case CERCA_GEO_ESTADO.NO_SOPORTADA:
        return (
          <div>
            {controles}
            <div style={{ marginTop: 16 }}>
              <EmptyState
                title={t('pages.cobrador.cerca.geoNoSoportada')}
                description={t('pages.cobrador.cerca.geoNoSoportadaDescripcion')}
              />
            </div>
          </div>
        );
      case CERCA_GEO_ESTADO.ERROR:
        return (
          <div>
            {controles}
            <div style={{ marginTop: 16 }}>
              <EmptyState
                title={t('pages.cobrador.cerca.geoError')}
                description={t('pages.cobrador.cerca.geoErrorDescripcion')}
                action={
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => capturar()}
                  >
                    {t('common.retry')}
                  </Button>
                }
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  }

  const ubicacionCapturada = ubicacion as CercaUbicacion;
  const mapa = (
    <DashboardCard
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <SectionHeader
            title={t('pages.cobrador.cerca.mapaTitle')}
            subtitle={t('pages.cobrador.cerca.mapaSubtitle')}
          />
        </div>
      }
      bodyStyle={{ padding: 0 }}
      style={{ height: '100%' }}
    >
      {cercaData === undefined ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <CercaMapa
          ubicacion={ubicacionCapturada}
          radioMetros={radioMetros}
          clientes={clientes}
          selectedClienteId={cobrarCliente?.idCliente ?? null}
          onSelect={onSeleccionar}
        />
      )}
    </DashboardCard>
  );

  const lista = (
    <DashboardCard
      className="cerca-card--lista"
      title={t('pages.cobrador.cerca.listaTitulo')}
      subtitle={t('pages.cobrador.cerca.listaSubtitle', {
        count: cercaData?.total ?? 0,
        radio: formatDistancia(radioMetros),
      })}
      style={{ height: '100%' }}
      bodyStyle={{ padding: 12 }}
    >
      {cercaData === undefined ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : clientes.length === 0 ? (
        <EmptyState
          compact
          title={t('pages.cobrador.cerca.emptyTitulo')}
          description={t('pages.cobrador.cerca.emptyDescripcion')}
        />
      ) : (
        <div className="cerca-list" role="list" aria-label={t('pages.cobrador.cerca.listaTitulo')}>
          {clientes.map((cliente) => (
            <CercaRow
              key={cliente.idCliente}
              cliente={cliente}
              selected={cobrarCliente?.idCliente === cliente.idCliente}
              onSelect={() => onSeleccionar(cliente)}
              rowRef={(node) => {
                if (node) rowRefs.current.set(cliente.idCliente, node);
                else rowRefs.current.delete(cliente.idCliente);
              }}
            />
          ))}
        </div>
      )}
    </DashboardCard>
  );

  return (
    <div>
      {controles}
      {cercaQuery.isError && cercaData !== undefined && (
        <Alert
          type="warning"
          showIcon
          message={t('dashboard.rutaCobros.refreshError')}
          style={{ marginTop: 16 }}
        />
      )}
      <div className="cerca-grid" style={{ marginTop: cercaQuery.isError && cercaData !== undefined ? 8 : 16 }}>
        {cercaQuery.isError && cercaData === undefined ? (
          <DashboardCard style={{ gridColumn: '1 / -1' }}>
            <EmptyState
              title={t('pages.cobrador.cerca.cargaError')}
              description={t('pages.cobrador.cerca.cargaErrorDescripcion')}
              action={
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => void cercaQuery.refetch()}
                >
                  {t('common.retry')}
                </Button>
              }
            />
          </DashboardCard>
        ) : (
          <>
            {mapa}
            {lista}
          </>
        )}
      </div>
      <PagoCreateModal
        open={cobrarCliente !== null}
        preselectClienteId={cobrarCliente?.idCliente}
        preselectedCliente={cobrarCliente ? cercaRowToClienteRef(cobrarCliente) : undefined}
        onClose={() => setCobrarCliente(null)}
        onSuccess={refrescar}
      />
    </div>
  );
}
